import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DefaultViewHeader from "../../components/main/default_view_header/DefaultViewHeader";
import MainBtn from "../../components/main/main_btn/MainBtn";
import "./style.scss";
import { App, Input, Select, Switch, Tooltip } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone, InfoCircleOutlined } from "@ant-design/icons";
import AuthContext from "../../contexts/AuthContext";
import axios from "../../services/axios";

type Installer = {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
};

const Account = () => {
  const { t } = useTranslation("home");
  const { user, update_user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const { notification } = App.useApp();
  const [formData, setFormData] = useState({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    language: user.language,
    current_password: "",
    new_password: "",
    show_customer_identity: user.show_customer_identity,
  });
  const [installers, setInstallers] = useState<Installer[]>([]);
  const [selectedInstaller, setSelectedInstaller] = useState<number | null>(null);
  const [installerLoading, setInstallerLoading] = useState(false);

  const can_save = useMemo(() => {
    const baseline = {
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      language: user.language,
      current_password: "",
      new_password: "",
      show_customer_identity: user.show_customer_identity,
    };
    return JSON.stringify(formData) !== JSON.stringify(baseline);
  }, [formData, user]);

  const handle_update = async () => {
    setLoading(true);
    const message = await update_user(formData);

    if (message.type === "error") {
      notification.error({
        message: message.message,
        description: message.description,
        placement: "topLeft",
      });
    } else {
      notification.success({
        message: message.message,
        description: message.description,
        placement: "topLeft",
      });
    }

    setLoading(false);
  };

  const handle_reset = () => {
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      language: user.language,
      current_password: "",
      new_password: "",
      show_customer_identity: user.show_customer_identity,
    });
  };

  const fetchInstallers = async () => {
    try {
      const response = await axios.get("/api-v1/users/all-installers/");
      setInstallers(response.data);
    } catch (error) {
      console.error("Failed to fetch installers:", error);
    }
  };

  const handleInstallerRequest = async () => {
    if (!selectedInstaller) {
      notification.error({
        message: "Please select an installer",
        placement: "topLeft",
      });
      return;
    }

    setInstallerLoading(true);
    try {
      const response = await axios.post("/api-v1/users/request-installer-assignment/", {
        installer_id: selectedInstaller
      });
      
      notification.success({
        message: response.data.message,
        placement: "topLeft",
      });
      
      // Refresh user data
      window.location.reload();
    } catch (error: any) {
      notification.error({
        message: error.response?.data?.error || "Failed to request installer assignment",
        placement: "topLeft",
      });
    }
    setInstallerLoading(false);
  };

  useEffect(() => {
    handle_reset();
    fetchInstallers();
  }, [user]);

  return (
    <div className="account-main-view">
      <DefaultViewHeader
        title={t("account_title")}
        description={t("account_description")}
      />
      <div className="center-view">
        <form className="center-view-form classic-form">
          <div className="input-side">
            <div className="input-block">
              <label htmlFor="language-selector">
                {t("site_language_label")}
              </label>
              <Select
                id="language-selector"
                size="large"
                style={{ width: "100%" }}
                // defaultValue={i18n.language.split("-")[0]}
                // onChange={(e) => i18n.changeLanguage(e)}
                value={formData.language}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    language: e,
                  })
                }
              >
                <Select.Option value="fr">Francais</Select.Option>
                <Select.Option value="en">English</Select.Option>
                <Select.Option value="de">Deutsch</Select.Option>
                <Select.Option value="nl">Nederlands</Select.Option>
              </Select>
            </div>
            <div className="row">
              <div className="input-block">
                <label htmlFor="first-name-input">
                  {t("first_name_label")}
                </label>
                <Input
                  size="small"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      first_name: e.target.value,
                    })
                  }
                  placeholder={t("first_name_placeholder")}
                  type="text"
                  autoComplete="given-name"
                  id="first-name-input"
                />
              </div>
              <div className="input-block">
                <label htmlFor="last-name-input"> {t("last_name_label")}</label>
                <Input
                  size="small"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      last_name: e.target.value,
                    })
                  }
                  placeholder={t("last_name_placeholder")}
                  type="text"
                  autoComplete="family-name"
                  id="last-name-input"
                />
              </div>
            </div>
            {user.is_installer && (
              <div className="input-block account-installer-identity">
                <label
                  htmlFor="show-customer-identity-switch"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    margin: 0,
                  }}
                >
                  {t("show_customer_identity_label")}
                  <Tooltip title={t("show_customer_identity_hint")}>
                    <InfoCircleOutlined
                      style={{
                        color: "#012124",
                        opacity: 0.45,
                        cursor: "help",
                        fontSize: "14px",
                      }}
                    />
                  </Tooltip>
                </label>
                <div style={{ alignSelf: "flex-start", width: "fit-content" }}>
                  <Switch
                    id="show-customer-identity-switch"
                    checked={formData.show_customer_identity}
                    onChange={(checked) =>
                      setFormData({
                        ...formData,
                        show_customer_identity: checked,
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
          <div className="input-side">
            <div className="input-block">
              <label htmlFor="email-input">{t("email_label")}</label>
              <Input
                size="small"
                value={formData.email}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    email: e.target.value,
                  })
                }
                disabled={true}
                placeholder={t("email_placeholder")}
                type="email"
                autoComplete="email"
                id="email-input"
              />
            </div>
            <div className="input-block">
              <label htmlFor="password-input">
                {t("current_password_label")}
              </label>
              <Input.Password
                size="small"
                value={formData.current_password}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    current_password: e.target.value,
                  })
                }
                placeholder={t("current_password_placeholder")}
                type="password"
                autoComplete="password"
                id="password-input"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </div>

            <div className="input-block">
              <label htmlFor="password-confirmation-input">
                {t("new_password_label")}
              </label>
              <Input.Password
                size="small"
                value={formData.new_password}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    new_password: e.target.value,
                  })
                }
                placeholder={t("new_password_placeholder")}
                type="password"
                autoComplete="password"
                id="password-confirmation-input"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </div>
          </div>
        </form>

        <div className="center-view-form-submit">
          <MainBtn
            title={t("save_updates")}
            onClick={handle_update}
            disabled={!can_save}
            loading={loading}
          />
          <MainBtn
            title={t("main:cancel")}
            color="secondary"
            onClick={handle_reset}
            disabled={!can_save}
          />
        </div>

        {!user.is_installer && (
          <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
              {t('installer_assignment_title')}
            </h3>
            {user.installer_name ? (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ color: '#495057', marginBottom: '8px' }}>
                  {t('current_installer_label')}: <strong>{user.installer_name}</strong>
                </p>
              </div>
            ) : (
              <p style={{ color: '#6c757d', marginBottom: '16px' }}>
                {t('no_installer_assigned')}
              </p>
            )}
            {user.pending_installer_name && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                backgroundColor: '#fff3cd', 
                borderRadius: '4px',
                border: '1px solid #ffc107'
              }}>
                <p style={{ color: '#856404', marginBottom: '0' }}>
                  ⏳ {t('pending_assignment_label')}: <strong>{user.pending_installer_name}</strong>
                </p>
                <p style={{ color: '#856404', fontSize: '13px', marginTop: '4px', marginBottom: '0' }}>
                  {t('pending_assignment_description')}
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  {t('select_installer_label')}
                </label>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('select_installer_placeholder')}
                  value={selectedInstaller}
                  onChange={setSelectedInstaller}
                >
                  {installers.map(installer => (
                    <Select.Option key={installer.id} value={installer.id}>
                      {installer.first_name} {installer.last_name} ({installer.email})
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <MainBtn
                title={t('request_assignment')}
                onClick={handleInstallerRequest}
                disabled={!selectedInstaller}
                loading={installerLoading}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Account;
